import { createGroundElevation } from './groundElevation';
import type { LandHeight } from './types';
import type { Layout } from '../data/types';
import type { AABB, Vec2 } from '../geo/types';
import { createSpatialHash } from '../geo/spatialHash';
import { createCloisterFloor } from './cloisterFloor';
import { createWaterGateDeck } from './waterGateDeck';
import { damDeckHeight } from './damDeck';
import { pointInRing, ringBounds } from '../geo/polygon';
import type { SpatialHash } from '../geo/spatialHash';

// A point query owns one hash cell. Reuse that cell's immutable candidates rather than building
// an AABB, Set and array for every vertex and interior height probe.
function pointCandidates<T>(hash: SpatialHash<T>, cellSize: number): (p: Vec2) => readonly T[] {
  const columns = new Map<number, Map<number, readonly T[]>>();
  return (p: Vec2) => {
    const x = Math.floor(p[0] / cellSize),
      y = Math.floor(p[1] / cellSize);
    let column = columns.get(x);
    const known = column?.get(y);
    if (known) return known;
    const cx = (x + 0.5) * cellSize,
      cy = (y + 0.5) * cellSize;
    const candidates = hash.query({ minX: cx, maxX: cx, minY: cy, maxY: cy });
    if (!column) {
      column = new Map();
      columns.set(x, column);
    }
    column.set(y, candidates);
    return candidates;
  };
}

interface PreparedSegment {
  readonly a: Vec2;
  readonly dx: number;
  readonly dy: number;
  readonly lengthSq: number;
}
function prepareSegment(a: Vec2, b: Vec2): PreparedSegment {
  const dx = b[0] - a[0],
    dy = b[1] - a[1];
  return { a, dx, dy, lengthSq: dx * dx + dy * dy };
}
function distanceTo(p: Vec2, leg: PreparedSegment): number {
  const px = p[0] - leg.a[0],
    py = p[1] - leg.a[1];
  if (leg.lengthSq < 1e-10) return Math.sqrt(px * px + py * py);
  const share = Math.max(0, Math.min(1, (px * leg.dx + py * leg.dy) / leg.lengthSq));
  const x = p[0] - (leg.a[0] + share * leg.dx),
    y = p[1] - (leg.a[1] + share * leg.dy);
  return Math.sqrt(x * x + y * y);
}

// A short reconstructed drainage transition meets the level masonry without flattening the square.
const DAM_CROWN_TRANSITION_M = 4;
interface DeckTransition {
  readonly polygon: readonly Vec2[];
  readonly box: AABB;
  readonly edges: readonly PreparedSegment[];
}
function deckCrownWeight(p: Vec2, decks: readonly DeckTransition[]): number {
  let weight = 1;
  for (const deck of decks) {
    const box = deck.box;
    if (p[0] < box.minX || p[0] > box.maxX || p[1] < box.minY || p[1] > box.maxY) continue;
    if (pointInRing(p, deck.polygon)) return 0;
    let distance = DAM_CROWN_TRANSITION_M;
    for (const edge of deck.edges) distance = Math.min(distance, distanceTo(p, edge));
    const t = distance / DAM_CROWN_TRANSITION_M;
    weight = Math.min(weight, t * t * (3 - 2 * t));
  }
  return weight;
}

/** Conservative reconstructed paving profiles, not a historical elevation survey. Edges remain
 * at the buildings' foundation level; the centre sheds rain toward the sides. */
export function createStreetRelief(
  layout: Layout,
  options: { readonly damDeck?: boolean; readonly cloisterFloor?: boolean } = {}
): LandHeight {
  const elevation = createGroundElevation(layout);
  const cloisterFloor = createCloisterFloor(layout);
  const waterGateDeck = createWaterGateDeck(layout);
  const hash = createSpatialHash<PreparedSegment & { half: number }>(20);
  for (const street of layout.streets) {
    if (street.kind === 'dike') continue;
    const half = street.width / 2;
    for (let i = 1; i < street.centerline.length; i++) {
      const a = street.centerline[i - 1]! as Vec2;
      const b = street.centerline[i]! as Vec2;
      hash.insert(
        { ...prepareSegment(a, b), half },
        {
          minX: Math.min(a[0], b[0]) - half,
          maxX: Math.max(a[0], b[0]) + half,
          minY: Math.min(a[1], b[1]) - half,
          maxY: Math.max(a[1], b[1]) + half,
        }
      );
    }
  }
  const landings = createSpatialHash<Vec2>(20);
  for (const bridge of layout.bridges)
    for (const end of [bridge.a, bridge.b]) {
      landings.insert(end, {
        minX: end[0] - 5,
        maxX: end[0] + 5,
        minY: end[1] - 5,
        maxY: end[1] + 5,
      });
    }
  const riverDams = layout.landmarks.filter((l) => l.kind === 'dam');
  const dams = options.damDeck === false ? [] : riverDams;
  const deckTransitions = riverDams.map((dam): DeckTransition => {
    const box = ringBounds(dam.footprint);
    return {
      polygon: dam.footprint,
      box: {
        minX: box.minX - DAM_CROWN_TRANSITION_M,
        maxX: box.maxX + DAM_CROWN_TRANSITION_M,
        minY: box.minY - DAM_CROWN_TRANSITION_M,
        maxY: box.maxY + DAM_CROWN_TRANSITION_M,
      },
      edges: dam.footprint.map((a, i) =>
        prepareSegment(a, dam.footprint[(i + 1) % dam.footprint.length]!)
      ),
    };
  });
  const legsAt = pointCandidates(hash, 20),
    landingsAt = pointCandidates(landings, 20);
  const squares = layout.squares.map((square) => ({
    polygon: square.polygon,
    box: ringBounds(square.polygon),
    edges: square.polygon.map((a, i) =>
      prepareSegment(a, square.polygon[(i + 1) % square.polygon.length]!)
    ),
  }));
  return Object.assign(
    (p: Vec2) => {
      for (const dam of dams) {
        const height = damDeckHeight(dam, p);
        if (height !== null) return height;
      }
      const floor = options.cloisterFloor === false ? null : cloisterFloor(p);
      if (floor !== null) return floor;
      let y = 0;
      for (const leg of legsAt(p)) {
        const share = Math.max(0, 1 - distanceTo(p, leg) / leg.half);
        y = Math.max(y, Math.min(0.14, leg.half * 0.04) * share * share);
      }
      for (const square of squares) {
        const box = square.box;
        if (p[0] < box.minX || p[0] > box.maxX || p[1] < box.minY || p[1] > box.maxY) continue;
        if (!pointInRing(p, square.polygon)) continue;
        let edge = Infinity;
        for (const leg of square.edges) edge = Math.min(edge, distanceTo(p, leg));
        y = Math.max(y, 0.18 * Math.min(1, edge / 8));
      }
      // Meet flat bank landings continuously. The raised bridges start their ramps on the deck.
      for (const end of landingsAt(p)) {
        y *= Math.min(1, Math.hypot(p[0] - end[0], p[1] - end[1]) / 5);
      }
      // Both the walker and decorative paving meet the same flat Dam crest at its perimeter.
      if (y > 0) y *= deckCrownWeight(p, deckTransitions);
      return elevation(p) + Math.max(y, waterGateDeck(p) ?? 0);
    },
    elevation.elevationBounds ? { elevationBounds: elevation.elevationBounds } : {}
  );
}
